import React, { useState, useEffect, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { translateRoomType } from '../../../constants/roomTypes';

const PaymentModal = ({ 
  isOpen, 
  onClose, 
  reservation, 
  onProcessPayment,
  loading = false 
}) => {
  const [formData, setFormData] = useState({
    amount: '',
    paymentMethod: 'PIX',
    status: 'PAID',
    transactionId: '',
    paymentDate: '',
    notes: '',
    discountAmount: '0'
  });

  const [errors, setErrors] = useState({});

  // Calcular desconto da reserva uma única vez usando useMemo
  const reservationDiscount = useMemo(() => {
    if (!reservation) return 0;
    return reservation.discountAmount 
      ? (typeof reservation.discountAmount === 'string' 
        ? parseFloat(reservation.discountAmount) 
        : Number(reservation.discountAmount)) 
      : 0;
  }, [reservation?.discountAmount]);

  // Atualizar formData quando reservation mudar
  useEffect(() => {
    if (reservation) {
      const originalAmount = Number(reservation.totalAmount || 0);
      const finalAmount = originalAmount - reservationDiscount;
      
      setFormData({
        amount: finalAmount > 0 ? finalAmount : originalAmount,
        paymentMethod: 'PIX',
        status: 'PAID',
        transactionId: '',
        paymentDate: new Date().toISOString().split('T')[0],
        notes: '',
        discountAmount: '' // Resetar desconto adicional para evitar conflito
      });
    }
  }, [reservation, reservationDiscount]);

  // Função para formatar valor monetário
  const formatCurrencyInput = (value) => {
    if (!value) return '';
    
    // Remove tudo exceto números
    const numbers = value.replace(/\D/g, '');
    
    if (numbers === '') return '';
    
    // Se começar com zero e tiver mais números, remove o zero inicial
    let cleanedNumbers = numbers;
    if (cleanedNumbers.length > 1 && cleanedNumbers[0] === '0') {
      cleanedNumbers = cleanedNumbers.substring(1);
    }
    
    // Converte para número e divide por 100 para ter centavos
    const numberValue = parseFloat(cleanedNumbers) / 100;
    
    // Formata com 2 casas decimais e substitui ponto por vírgula
    return numberValue.toFixed(2).replace('.', ',');
  };

  const handleInputChange = (field, value) => {
    // Formatação automática para desconto (moeda)
    if (field === 'discountAmount') {
      value = formatCurrencyInput(value);
    }
    
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Limpar erro do campo quando alterado
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.amount || formData.amount <= 0) {
      newErrors.amount = 'Valor é obrigatório e deve ser maior que zero';
    }

    if (!formData.paymentMethod) {
      newErrors.paymentMethod = 'Método de pagamento é obrigatório';
    }

    if (!formData.status) {
      newErrors.status = 'Status do pagamento é obrigatório';
    }

    if (!formData.paymentDate) {
      newErrors.paymentDate = 'Data do pagamento é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      // Converter desconto formatado de volta para número
      const discountAmount = typeof formData.discountAmount === 'string' 
        ? parseFloat(formData.discountAmount.replace(',', '.')) || 0
        : Number(formData.discountAmount || 0);
      const finalAmount = Number(formData.amount) - discountAmount;
      
      const paymentData = {
        ...formData,
        amount: finalAmount > 0 ? finalAmount : Number(formData.amount), // Garantir que não fique negativo
        discountAmount: discountAmount,
        paymentDate: new Date(formData.paymentDate)
      };

      await onProcessPayment(reservation.id, paymentData);
      onClose();
    } catch (error) {
      console.error('Erro ao processar pagamento:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount);
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      'CASH': 'Dinheiro',
      'CREDIT_CARD': 'Cartão de Crédito',
      'DEBIT_CARD': 'Cartão de Débito',
      'PIX': 'PIX',
      'BANK_TRANSFER': 'Transferência Bancária'
    };
    return labels[method] || method;
  };

  const getPaymentStatusLabel = (status) => {
    const labels = {
      'PENDING': 'Pendente',
      'PAID': 'Pago',
      'OVERDUE': 'Em Atraso',
      'CANCELLED': 'Cancelado',
      'REFUNDED': 'Reembolsado'
    };
    return labels[status] || status;
  };

  // Calcular valores para o resumo usando useMemo
  const reservationSummary = useMemo(() => {
    if (!reservation) return { originalAmount: 0, discount: 0, finalAmount: 0 };
    
    const originalAmount = Number(reservation.totalAmount);
    const discount = reservation.discountAmount 
      ? (typeof reservation.discountAmount === 'string' 
        ? parseFloat(reservation.discountAmount) 
        : Number(reservation.discountAmount)) 
      : 0;
    const finalAmount = originalAmount - discount;
    
    return { originalAmount, discount, finalAmount };
  }, [reservation?.totalAmount, reservation?.discountAmount]);

  if (!isOpen || !reservation) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              Processar Pagamento
            </h2>
            <p className="text-sm text-muted-foreground">
              Reserva #{reservation.id} - {reservation.guestName}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="XCircle" size={20} color="var(--color-muted-foreground)" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="space-y-6">
            {/* Informações da Reserva */}
            <div className="bg-muted/30 rounded-lg p-4">
              <h4 className="font-medium text-foreground mb-3">Detalhes da Reserva</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Hóspede:</span>
                  <div className="font-medium text-foreground">{reservation.guestName}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Quarto:</span>
                  <div className="font-medium text-foreground">
                    {reservation.room?.number || reservation.roomNumber || 'N/A'} - {translateRoomType(reservation.room?.type || reservation.roomType) || 'N/A'}
                  </div>
                </div>
                <div>
                  <span className="text-muted-foreground">Check-in:</span>
                  <div className="font-medium text-foreground">
                    {new Date(reservation.checkInDate).toLocaleDateString('pt-BR')}
                  </div>
                </div>
                <div>
                  <span className="text-muted-foreground">Check-out:</span>
                  <div className="font-medium text-foreground">
                    {new Date(reservation.checkOutDate).toLocaleDateString('pt-BR')}
                  </div>
                </div>
                <div className="col-span-2">
                  <span className="text-muted-foreground">Valor Total da Reserva:</span>
                  {reservationSummary.discount > 0 ? (
                    <div>
                      <div className="font-semibold text-lg text-success">{formatCurrency(reservationSummary.finalAmount)}</div>
                      <div className="text-xs text-muted-foreground line-through">
                        {formatCurrency(reservationSummary.originalAmount)} (- {formatCurrency(reservationSummary.discount)} desconto)
                      </div>
                    </div>
                  ) : (
                    <div className="font-semibold text-lg text-success">{formatCurrency(reservationSummary.originalAmount)}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Campos do Pagamento */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Valor do Pagamento"
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder="0.00"
                  value={formData.amount}
                  onChange={(e) => handleInputChange('amount', e.target.value)}
                  error={errors.amount}
                  required
                />
                
                <Input
                  label="Data do Pagamento"
                  type="date"
                  value={formData.paymentDate}
                  onChange={(e) => handleInputChange('paymentDate', e.target.value)}
                  error={errors.paymentDate}
                  required
                />
              </div>
              
              <Input
                label="Desconto Adicional (R$)"
                type="text"
                placeholder="0,00"
                value={formData.discountAmount}
                onChange={(e) => handleInputChange('discountAmount', e.target.value)}
                description="Desconto adicional a ser aplicado neste pagamento"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Método de Pagamento"
                  value={formData.paymentMethod}
                  onChange={(value) => handleInputChange('paymentMethod', value)}
                  options={[
                    { value: 'PIX', label: 'PIX' },
                    { value: 'CREDIT_CARD', label: 'Cartão de Crédito' },
                    { value: 'DEBIT_CARD', label: 'Cartão de Débito' },
                    { value: 'CASH', label: 'Dinheiro' },
                    { value: 'BANK_TRANSFER', label: 'Transferência Bancária' }
                  ]}
                  error={errors.paymentMethod}
                  required
                />

                <Select
                  label="Status do Pagamento"
                  value={formData.status}
                  onChange={(value) => handleInputChange('status', value)}
                  options={[
                    { value: 'PAID', label: 'Pago' },
                    { value: 'PENDING', label: 'Pendente' },
                    { value: 'OVERDUE', label: 'Em Atraso' },
                    { value: 'CANCELLED', label: 'Cancelado' },
                    { value: 'REFUNDED', label: 'Reembolsado' }
                  ]}
                  error={errors.status}
                  required
                />
              </div>

              <Input
                label="ID da Transação (Opcional)"
                type="text"
                placeholder="Ex: PIX_123456, CARD_789012"
                value={formData.transactionId}
                onChange={(e) => handleInputChange('transactionId', e.target.value)}
                description="Identificador único da transação no sistema de pagamento"
              />

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Observações (Opcional)
                </label>
                <textarea
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Observações sobre o pagamento, comprovante, etc..."
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                />
              </div>
            </div>

            {/* Resumo do Pagamento */}
            {formData.amount && formData.paymentDate && (
              <div className="bg-primary/10 rounded-lg p-4">
                <h4 className="font-medium text-foreground mb-3">Resumo do Pagamento</h4>
                <div className="space-y-2 text-sm">
                  {(() => {
                    // Se há desconto na reserva, mostrar valor original cortado
                    if (reservationSummary.discount > 0) {
                      return (
                        <div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Valor da Reserva:</span>
                            <span className="text-xs text-muted-foreground line-through">
                              {formatCurrency(reservationSummary.originalAmount)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Desconto da Reserva:</span>
                            <span className="font-semibold text-error">
                              - {formatCurrency(reservationSummary.discount)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Valor do Pagamento:</span>
                            <span className="font-semibold text-foreground">
                              {formatCurrency(Number(formData.amount))}
                            </span>
                          </div>
                        </div>
                      );
                    }
                    return (
                  <div className="flex justify-between">
                        <span className="text-muted-foreground">Valor do Pagamento:</span>
                    <span className="font-semibold text-foreground">
                      {formatCurrency(Number(formData.amount))}
                    </span>
                  </div>
                    );
                  })()}
                  {(() => {
                    const discountValue = typeof formData.discountAmount === 'string' 
                      ? parseFloat(formData.discountAmount.replace(',', '.')) || 0
                      : Number(formData.discountAmount || 0);
                    return discountValue > 0;
                  })() && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Desconto adicional:</span>
                        <span className="font-semibold text-error">
                          - {(() => {
                            const discountValue = typeof formData.discountAmount === 'string' 
                              ? parseFloat(formData.discountAmount.replace(',', '.')) || 0
                              : Number(formData.discountAmount || 0);
                            return formatCurrency(discountValue);
                          })()}
                        </span>
                      </div>
                      <div className="pt-2 border-t border-primary/30">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground font-semibold">Valor Final:</span>
                          <span className="font-bold text-lg text-success">
                            {(() => {
                              const discountValue = typeof formData.discountAmount === 'string' 
                                ? parseFloat(formData.discountAmount.replace(',', '.')) || 0
                                : Number(formData.discountAmount || 0);
                              return formatCurrency(Number(formData.amount) - discountValue);
                            })()}
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Método:</span>
                    <span className="text-foreground">
                      {getPaymentMethodLabel(formData.paymentMethod)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status:</span>
                    <span className="text-foreground">
                      {getPaymentStatusLabel(formData.status)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Data:</span>
                    <span className="text-foreground">
                      {new Date(formData.paymentDate).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                  {formData.transactionId && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Transação:</span>
                      <span className="font-mono text-xs text-foreground">
                        {formData.transactionId}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <div className="text-sm text-muted-foreground">
            Todos os campos marcados com * são obrigatórios
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="outline" onClick={onClose} disabled={loading}>
              Cancelar
            </Button>
            <Button 
              variant="default" 
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Processando...</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                    <line x1="1" y1="10" x2="23" y2="10"/>
                  </svg>
                  <span>Processar Pagamento</span>
                </div>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
